import styled from 'styled-components'

import { Dialog } from 'components/Dialog/Dialog'
import { Typography } from 'components/Typography/Typography'
import { Button } from 'components/Button/Button'
import { Collateral } from './Collateral'
import { IconInfo } from 'components/Icons/InfoIcon'
import { useIsMobile } from 'utils/useIsMobile'
import { usePositionBalances } from './hooks/usePositionBalances'
import { PhantomTypeArgument, StructClass, TypeArgument } from '@/shared/gen/_framework/reified'
import { Position, ClmmPool } from '@kunalabs-io/kai'
import { useCallback, useMemo, useState } from 'react'
import { SLIPPAGE } from 'utils/const'
import { useExecute } from 'utils/useExecute'
import { Spinner } from 'components/Spinner/Spinner'
import { ReversedWrapper } from 'components/ReversedWrapper/ReversedWrapper'
import { useRouter } from 'contexts/SwapRouterContext'
import { SelectSwapAggregatorMenu } from './SelectSwapAggregatorMenu'
import { Tooltip } from 'components/Tooltip/Tooltip'
import { useCurrentWalletAddress } from 'utils/useCurrentWalletAddress'
import { LP_POSITION_DEPOSIT } from 'utils/sentry-utils'
import { usePreProcessedPositionUpdate } from './hooks/usePreProcessedPositionUpdate'

const SectionLabel = styled(Typography)`
  display: flex;
  align-items: center;
  margin-top: 16px;
  margin-bottom: 16px;
  svg {
    cursor: pointer;
    display: flex;
    margin-left: 6px;
    path {
      stroke: ${p => p.theme.colors.text.secondary};
    }
  }
`

const PositionBreakdownContainer = styled.div`
  padding: 20px 16px;
  border-radius: 8px;
  border: 1px solid ${p => p.theme.colors.border};
  background: ${p => p.theme.colors.background.secondary};
`

const PositionBreakdownContainerItem = styled.div`
  display: flex;
  justify-content: space-between;
`

const PositionBreakdownSymbol = styled(Typography)`
  display: flex;
  img {
    margin-right: 8px;
    border-radius: 50%;
  }
`

interface Props {
  position: Position<PhantomTypeArgument, PhantomTypeArgument, TypeArgument>
  positionCapId: string
  pool?: ClmmPool<StructClass, unknown, PhantomTypeArgument, PhantomTypeArgument>
  isOpen: boolean
  onClose: () => void
  onDepositSuccess: () => void
  onDepositProcessed?: () => void
  isReversedPair: boolean
}

export const DepositDialog = ({
  isOpen,
  onClose,
  position,
  positionCapId,
  pool,
  onDepositSuccess,
  onDepositProcessed,
  isReversedPair,
}: Props) => {
  const [isLoadingDeposit, setIsLoadingDeposit] = useState(false)

  const walletAddress = useCurrentWalletAddress()
  const isMobile = useIsMobile()

  const execute = useExecute()
  const preProcessedPositionUpdate = usePreProcessedPositionUpdate()

  const { router } = useRouter()

  const {
    xInfo,
    sanitizedXValue,
    xCollateral,
    xCollateralError,
    xCollateralDollars,
    handleXChange,
    handleMaxClickX,
    handleHalfClickX,
    yInfo,
    sanitizedYValue,
    yCollateral,
    yCollateralError,
    yCollateralDollars,
    handleYChange,
    handleMaxClickY,
    handleHalfClickY,
  } = usePositionBalances(position.configInfo)

  const configInfo = position.configInfo

  const submitDisabled = (!xCollateral && !yCollateral) || xCollateralError || yCollateralError

  const onDepositClick = useCallback(async () => {
    if (!walletAddress || !pool || !router || isLoadingDeposit) {
      return
    }

    setIsLoadingDeposit(true)
    const xAmt = xCollateral || position.X.newAmount(0n)
    const yAmt = yCollateral || position.Y.newAmount(0n)
    if (yAmt.int === 0n && xAmt.int === 0n) {
      throw new Error('both deposit amounts cannot be 0')
    }

    void execute(
      () =>
        position.deposit(
          router,
          {
            pool,
            positionCapId,
            xAmt,
            yAmt,
            slippage: SLIPPAGE,
          },
          walletAddress
        ),
      {
        transactionType: LP_POSITION_DEPOSIT,
        sentryContext: {
          positionId: position.id,
          walletAddress: walletAddress || '',
          xInputValue: sanitizedXValue,
          yInputValue: sanitizedYValue,
          poolId: pool.id,
          positionCapId,
          xAmt,
          yAmt,
          slippage: SLIPPAGE,
        },
        onSuccess: async () => {
          await preProcessedPositionUpdate(position.id)
          setIsLoadingDeposit(false)
          onDepositSuccess()
        },
        onProcessed: () => {
          onDepositProcessed?.()
        },
        onError: () => {
          setIsLoadingDeposit(false)
        },
      }
    )
  }, [
    walletAddress,
    pool,
    router,
    isLoadingDeposit,
    xCollateral,
    position,
    yCollateral,
    execute,
    positionCapId,
    onDepositSuccess,
    onDepositProcessed,
    sanitizedXValue,
    sanitizedYValue,
    preProcessedPositionUpdate,
  ])

  const depositResult = useMemo(() => {
    const xAmt = xCollateral || position.X.newAmount(0n)
    const yAmt = yCollateral || position.Y.newAmount(0n)

    if (!pool || (xAmt.int === 0n && yAmt.int === 0n)) {
      return undefined
    }

    return position.calcDepositResult({
      pool,
      xAmt: xCollateral || position.X.newAmount(0n),
      yAmt: yCollateral || position.Y.newAmount(0n),
    })
  }, [pool, position, xCollateral, yCollateral])

  return (
    <Dialog title="Deposit" isOpen={isOpen} onClose={onClose}>
      <div style={{ height: 24 }} />
      <Collateral
        xError={xCollateralError}
        xIcon={configInfo?.X.iconUrl}
        xSymbol={configInfo?.X.displaySymbol || ''}
        xBalance={xInfo}
        handleMaxClickX={handleMaxClickX}
        handleHalfClickX={handleHalfClickX}
        xDepositValue={sanitizedXValue}
        handleXChange={handleXChange}
        yIcon={configInfo?.Y.iconUrl}
        ySymbol={configInfo?.Y.displaySymbol || ''}
        yBalance={yInfo}
        yError={yCollateralError}
        handleMaxClickY={handleMaxClickY}
        handleHalfClickY={handleHalfClickY}
        yDepositValue={sanitizedYValue}
        handleYChange={handleYChange}
        style={{ marginTop: -16 }}
        isReversedPair={isReversedPair}
        xCollateralDollars={xCollateralDollars}
        yCollateralDollars={yCollateralDollars}
      />
      <div style={{ height: 40 }} />
      <SelectSwapAggregatorMenu />
      <Typography variant={isMobile ? 'description' : 'label1'} fontWeight="semiBold">
        Breakdown
      </Typography>

      <SectionLabel variant="body" color="secondary">
        Deposited amount
        <Tooltip
          content="Total funds you’re adding to your existing position."
          position="top"
          tooltipBoxStyles={{ marginLeft: 56 }}
        >
          <IconInfo width={16} height={16} />
        </Tooltip>
      </SectionLabel>
      <PositionBreakdownContainer>
        <ReversedWrapper
          childX={
            <PositionBreakdownContainerItem style={isReversedPair ? {} : { marginBottom: 20 }}>
              <PositionBreakdownSymbol variant="description" fontWeight="semiBold">
                <img src={configInfo?.X.iconUrl} width={24} height={24} alt={configInfo?.X.displaySymbol || ''} />
                {configInfo?.X.displaySymbol}
              </PositionBreakdownSymbol>
              <Typography variant="description" fontWeight="semiBold">
                {depositResult?.x.toString() || ''}
              </Typography>
            </PositionBreakdownContainerItem>
          }
          childY={
            <PositionBreakdownContainerItem style={isReversedPair ? { marginBottom: 20 } : {}}>
              <PositionBreakdownSymbol variant="description" fontWeight="semiBold">
                <img src={configInfo?.Y.iconUrl} width={24} height={24} alt={configInfo?.Y.displaySymbol || ''} />
                {configInfo?.Y.displaySymbol}
              </PositionBreakdownSymbol>
              <Typography variant="description" fontWeight="semiBold">
                {depositResult?.y.toString() || ''}
              </Typography>
            </PositionBreakdownContainerItem>
          }
          isReversed={isReversedPair}
        />
      </PositionBreakdownContainer>
      <Button
        onClick={onDepositClick}
        style={{
          marginTop: 40,
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 49,
        }}
        disabled={submitDisabled}
      >
        {isLoadingDeposit ? <Spinner style={{ marginTop: 0, marginLeft: 'inherit' }} /> : 'Deposit'}
      </Button>
    </Dialog>
  )
}
